import React from "react";
import { useCountry } from "../contexts/CountryContext";

const S = ({ title, children }) => (
  <section style={{ marginBottom: '28px' }}>
    <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '10px' }}>{title}</h2>
    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>{children}</div>
  </section>
);

const ProviderAgreementPage = () => {
  const { country } = useCountry();
  return (
  <div className="page" data-testid="provider-agreement-page" style={{ maxWidth: '800px', margin: '0 auto' }}>
    <div className="page-header">
      <h1>Provider Agreement</h1>
      <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Last updated: April 2026</p>
    </div>

    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '32px' }}>
      <p style={{ fontSize: '0.9375rem', color: 'var(--text-dark)', lineHeight: 1.7, marginBottom: '28px' }}>
        This Provider Agreement outlines the terms under which service professionals ("Providers") operate on the HandyHub platform. By registering as a provider, you agree to the following terms.
      </p>

      <S title="1. Eligibility">
        <p>To join HandyHub as a provider, you must:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Be at least 18 years of age</li>
          <li>Have the legal right to offer the services you list</li>
          <li>Hold any licences, registrations, certifications, or insurance required by law to operate in your trade and jurisdiction</li>
          <li>Provide accurate and truthful information during registration</li>
        </ul>
      </S>

      <S title="2. Independent Contractor Status">
        <p>Providers operate as independent contractors and not as employees, agents, or partners of HandyHub.</p>
        <p>HandyHub does not employ providers and is not responsible for the performance, quality, or outcome of services provided. Providers are solely responsible for how they deliver their services, and for maintaining any insurance, registrations, or licences required by law.</p>
      </S>

      <S title="3. Verification Requirements">
        <p>HandyHub asks each provider to submit a government-issued photo identification document during onboarding. We review every submission and grant the <strong>ID&nbsp;Verified</strong> badge once approved.</p>
        <p>HandyHub does not collect or verify business registration or insurance documents. You remain solely responsible for holding and maintaining any licences, registrations, or insurance required by law for your trade. Providing false or misleading information during onboarding may result in immediate account termination.</p>
        <p style={{ fontStyle: 'italic', color: '#6b7280', fontSize: '0.875rem', marginTop: '8px' }}>HandyHub reviews provider information during onboarding, however customers should independently assess providers before engaging services.</p>
      </S>

      <S title="4. Service Quality">
        <p>Providers agree to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Perform all work to a professional standard</li>
          <li>Accurately represent their skills, experience, and qualifications</li>
          <li>Deliver services as described in their quotes</li>
          <li>Address customer concerns promptly and professionally</li>
          <li>Comply with all relevant health and safety standards</li>
        </ul>
      </S>

      <S title="5. Communication Standards">
        <p>Providers are expected to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Respond to quote requests in a timely manner</li>
          <li>Communicate clearly and respectfully with customers</li>
          <li>Provide honest and accurate information in quotes and messages</li>
          <li>Notify customers promptly of any changes to schedule, pricing, or scope</li>
        </ul>
      </S>

      <S title="6. Pricing and Transparency">
        <p>Providers must:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Provide clear, itemised quotes where possible</li>
          <li>Disclose all costs upfront, including any potential additional charges</li>
          <li>Not engage in misleading or deceptive pricing practices</li>
          <li>Honour quoted prices unless a material change in scope is agreed upon with the customer</li>
        </ul>
      </S>

      <S title="7. Subscription and Platform Use">
        <p>Access to the HandyHub provider platform requires an active subscription. Providers agree to:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Pay subscription fees on time as per the selected plan</li>
          <li>Understand that subscription fees are non-refundable except as outlined in the Cancellation Policy</li>
          <li>Not attempt to bypass or circumvent the platform for the purpose of avoiding fees or lead tracking</li>
          <li>Use the platform in good faith and in accordance with its intended purpose</li>
        </ul>
      </S>

      <S title="8. Customer Relationships and Disputes">
        <p>Any agreement for services is between the provider and the customer.</p>
        <p>Providers are solely responsible for:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Fulfilling agreed services</li>
          <li>Resolving disputes with customers</li>
          <li>Handling refunds, adjustments, or service issues</li>
        </ul>
        <p>HandyHub may, at its discretion, provide support or mediation but is not a party to service agreements.</p>
      </S>

      <S title="9. Account Suspension or Termination">
        <p>HandyHub may suspend or terminate a provider's account for:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Violation of this agreement or the Terms of Service</li>
          <li>Fraudulent or misleading conduct</li>
          <li>Consistently poor reviews or customer complaints</li>
          <li>Failure to maintain required credentials or verification</li>
          <li>Non-payment of subscription fees</li>
          <li>Any behaviour that harms the platform, other users, or HandyHub's reputation</li>
        </ul>
        <p>Providers may also voluntarily close their account at any time by contacting support.</p>
      </S>

      <S title="10. Limitation of Liability">
        <p>To the maximum extent permitted by law, HandyHub shall not be liable for:</p>
        <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
          <li>Any loss, damage, or dispute arising from services provided by providers</li>
          <li>Indirect, incidental, or consequential damages</li>
        </ul>
        <p>Providers acknowledge that they use the platform at their own risk.</p>
      </S>

      <S title="11. Platform Rules and Updates">
        <p>Providers agree to comply with all HandyHub platform rules, including the Terms of Service, Privacy Policy, and any additional guidelines published on the platform.</p>
        <p>HandyHub reserves the right to update this agreement from time to time. Continued use of the platform constitutes acceptance of any updates.</p>
      </S>

      <S title="12. Contact">
        <p>For questions about this agreement, please contact: <a href={`mailto:${country.supportEmail}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }} data-testid="agreement-support-email">{country.supportEmail}</a></p>
      </S>
    </div>
  </div>
  );
};

export default ProviderAgreementPage;
